/**
 * BulkActionsBar
 *
 * Status-aware action bar rendered next to the work-orders filters when the
 * user has selected one or more rows. Three modes:
 *
 *   - Selection is entirely "new" → Accept, Reject, Request More Info
 *   - Selection is entirely another single status → one "Bulk Actions (TBD)"
 *     button that currently just logs (real actions land in a follow-up)
 *   - Selection has mixed statuses → disclaimer banner; no actions allowed
 *
 * We derive the status from each job's `work_order_status` string compared
 * case-insensitively so "New" / "new" / "NEW" all coalesce.
 */

import type { WorkOrder } from '@/domains/jobs/components/WorkOrdersList';
import { Button } from '@/shared/ui/button';
import { AlertTriangle, CheckCircle2, Settings2, XCircle } from 'lucide-react';
import { useMemo } from 'react';

export type BulkSelectionStatus =
  | { kind: 'new' }
  | { kind: 'single-other'; statusName: string }
  | { kind: 'mixed' };

export function deriveBulkSelectionStatus(jobs: WorkOrder[]): BulkSelectionStatus {
  if (jobs.length === 0) return { kind: 'mixed' }; // no-op — caller won't render

  const distinct = new Set(jobs.map((j) => (j.work_order_status ?? '').trim().toLowerCase()));

  if (distinct.size > 1) return { kind: 'mixed' };

  const only = [...distinct][0];
  if (only === 'new') return { kind: 'new' };

  const pretty = jobs[0].work_order_status ?? 'Unknown';
  return { kind: 'single-other', statusName: pretty };
}

interface BulkActionsBarProps {
  selectedJobs: WorkOrder[];
  onAccept: () => void;
  onReject: () => void;
}

export function BulkActionsBar({ selectedJobs, onAccept, onReject }: BulkActionsBarProps) {
  const status = useMemo(() => deriveBulkSelectionStatus(selectedJobs), [selectedJobs]);
  const count = selectedJobs.length;

  if (count === 0) return null;

  if (status.kind === 'mixed') {
    return (
      <div
        role="alert"
        className="flex items-center gap-2 rounded-md border border-amber-300 dark:border-amber-900/60 bg-amber-50 dark:bg-amber-950/30 px-3 py-2 text-sm text-amber-800 dark:text-amber-200"
      >
        <AlertTriangle className="w-4 h-4 shrink-0" />
        <span>
          Selected jobs must share the same status to run bulk actions. ({count} selected)
        </span>
      </div>
    );
  }

  if (status.kind === 'single-other') {
    return (
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            // Placeholder: real actions for non-"new" statuses ship in a follow-up.
            console.log('Bulk action (TBD) for status:', status.statusName, {
              ids: selectedJobs.map((j) => j.id),
            });
          }}
        >
          <Settings2 className="w-4 h-4 mr-2" />
          Bulk Actions (TBD) ({count})
        </Button>
      </div>
    );
  }

  // status.kind === 'new'
  return (
    <div className="flex items-center gap-2">
      <Button
        variant="outline"
        size="sm"
        className="border-red-600 text-red-600 hover:bg-red-50 dark:hover:bg-red-950"
        onClick={onReject}
      >
        <XCircle className="w-4 h-4 mr-2" />
        Reject ({count})
      </Button>
      <Button
        size="sm"
        className="text-white"
        style={{ backgroundColor: '#E84A2B' }}
        onClick={onAccept}
      >
        <CheckCircle2 className="w-4 h-4 mr-2" />
        Accept ({count})
      </Button>
    </div>
  );
}
